import type { UserWithRole } from 'better-auth/plugins/admin';
import { MoreVertical, Trash2, UserCog } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useRemoveUserMutation,
  useSetUserRoleMutation,
} from '@/hooks/useAdminUsers';

interface UserActionsMenuProps {
  user: UserWithRole;
  roleOptions: string[];
}

export function UserActionsMenu({ user, roleOptions }: UserActionsMenuProps) {
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(user.role ?? 'user');
  const defaultRole = user.role ?? 'user';
  const normalizedRoleOptions =
    roleOptions.length > 0 ? roleOptions : [defaultRole];

  useEffect(() => {
    if (isRoleDialogOpen) {
      setSelectedRole(defaultRole);
    }
  }, [defaultRole, isRoleDialogOpen]);

  const { mutate: changeRole, isPending: isUpdatingRole } =
    useSetUserRoleMutation({
      onSuccess: (_updatedUser, variables) => {
        toast.success(`Role updated to ${variables?.role} for ${user.email}.`);
        setIsRoleDialogOpen(false);
      },
      onError: (mutationError) => {
        toast.error(
          mutationError instanceof Error
            ? mutationError.message
            : 'Unable to update role.'
        );
      },
    });

  const { mutate: removeUser, isPending: isDeletingUser } =
    useRemoveUserMutation({
      onSuccess: () => {
        toast.success(`User ${user.email} deleted.`);
        setIsDeleteDialogOpen(false);
      },
      onError: (mutationError) => {
        toast.error(
          mutationError instanceof Error
            ? mutationError.message
            : 'Unable to delete user.'
        );
      },
    });

  const handleRoleDialogChange = (open: boolean) => {
    if (isUpdatingRole) return;
    setIsRoleDialogOpen(open);
  };

  const handleDeleteDialogChange = (open: boolean) => {
    if (isDeletingUser) return;
    setIsDeleteDialogOpen(open);
  };

  const hasRoleChanged = selectedRole !== defaultRole;
  const selectId = `role-select-${user.id}`;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 gap-1 px-3 text-xs font-semibold text-neutral-600"
            aria-label={`Open actions for ${user.email}`}>
            <MoreVertical className="h-4 w-4 text-neutral-500" />
            Actions
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={6}>
          <DropdownMenuItem onSelect={() => setIsRoleDialogOpen(true)}>
            <UserCog className="h-4 w-4 text-neutral-500" />
            Change role
          </DropdownMenuItem>
          <DropdownMenuItem
            className="text-red-600 focus:text-red-600"
            onSelect={() => setIsDeleteDialogOpen(true)}>
            <Trash2 className="h-4 w-4" />
            Delete user
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={isRoleDialogOpen} onOpenChange={handleRoleDialogChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change role</DialogTitle>
            <DialogDescription>
              Update the permissions for{' '}
              <span className="font-medium">{user.email}</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor={selectId}>Role</Label>
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger id={selectId}>
                <SelectValue aria-label={selectedRole} />
              </SelectTrigger>
              <SelectContent>
                {normalizedRoleOptions.map((role) => (
                  <SelectItem key={`${user.id}-${role}`} value={role}>
                    {role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsRoleDialogOpen(false)}
              disabled={isUpdatingRole}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() =>
                changeRole({ userId: user.id, role: selectedRole })
              }
              disabled={!hasRoleChanged || isUpdatingRole}>
              {isUpdatingRole ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={handleDeleteDialogChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete user</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove{' '}
              <span className="font-medium">{user.email}</span> and all of their
              sessions. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingUser}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeletingUser}
              onClick={(event) => {
                event.preventDefault();
                removeUser({ userId: user.id });
              }}>
              {isDeletingUser ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
